import { gql } from '@apollo/client';

/**
 * Organization Fragments
 */

export const ORGANIZATION_FRAGMENT = gql`
  fragment OrganizationFields on Organization {
    id
    name
    slug
    logo
    primary_color
    metadata
    createdAt
    updatedAt
    plan
    tenant_type
    industry
  }
`;

export const MEMBER_FRAGMENT = gql`
  fragment MemberFields on OrganizationMember {
    id
    userId
    organizationId
    role
    email
    invitedAt
    joinedAt
  }
`;

/**
 * Queries
 */

export const GET_MY_ORGANIZATIONS = gql`
  ${ORGANIZATION_FRAGMENT}
  query GetMyOrganizations {
    listOrganizations {
      ...OrganizationFields
      memberRole
    }
  }
`;

export const GET_ORGANIZATION = gql`
  ${ORGANIZATION_FRAGMENT}
  query GetOrganization($id: ID!) {
    organization(id: $id) {
      ...OrganizationFields
    }
  }
`;

export const GET_ORGANIZATION_BY_SLUG = gql`
  ${ORGANIZATION_FRAGMENT}
  query GetOrganizationBySlug($slug: String!) {
    organizationBySlug(slug: $slug) {
      ...OrganizationFields
    }
  }
`;

export const GET_ORGANIZATION_BY_CUSTOM_DOMAIN = gql`
  ${ORGANIZATION_FRAGMENT}
  query GetOrganizationByCustomDomain($customDomain: String!) {
    organizationByCustomDomain(customDomain: $customDomain) {
      ...OrganizationFields
    }
  }
`;

export const GET_MY_PERMISSIONS = gql`
  query GetMyPermissions {
    myPermissions {
      permission
      resource
      action
    }
  }
`;

export const GET_ORGANIZATION_MEMBERS = gql`
  ${MEMBER_FRAGMENT}
  query GetOrganizationMembers($organizationId: ID!) {
    organizationMembers(organizationId: $organizationId) {
      ...MemberFields
      user {
        id
        name
        email
      }
    }
  }
`;

/**
 * Mutations
 */

export const UPDATE_ORGANIZATION = gql`
  ${ORGANIZATION_FRAGMENT}
  mutation UpdateOrganization($id: ID!, $input: UpdateOrganizationInput!) {
    updateOrganization(id: $id, input: $input) {
      ...OrganizationFields
    }
  }
`;

export const INVITE_MEMBER = gql`
  mutation InviteMember($organizationId: ID!, $email: String!, $role: String!) {
    inviteMember(organizationId: $organizationId, email: $email, role: $role) {
      id
      email
      role
      status
      expiresAt
    }
  }
`;

export const REMOVE_MEMBER = gql`
  mutation RemoveMember($organizationId: ID!, $userId: ID!) {
    removeMember(organizationId: $organizationId, userId: $userId) {
      success
      message
    }
  }
`;

export const UPDATE_MEMBER_ROLE = gql`
  mutation UpdateMemberRole(
    $organizationId: ID!
    $userId: ID!
    $role: String!
  ) {
    updateMemberRole(
      organizationId: $organizationId
      userId: $userId
      role: $role
    ) {
      success
      message
    }
  }
`;

export const TENANT_FEATURES_FRAGMENT = gql`
  query TenantFeatures($tenantId: ID) {
    tenantFeatures(tenantId: $tenantId) {
      feature
      enabled
      hasAccess
      reason
      config
      enabledAt
      disabledAt
    }
  }
`;

export const GET_ORGANIZATION_ENABLED_FEATURES = gql`
  query TenantEnabledFeatures($tenantId: ID) {
    tenantEnabledFeatures(tenantId: $tenantId)
  }
`;

export const ORGANIZATION_OPERATION_COUNTRIES = gql`
  query OrganizationOperationCountries($organizationId: ID) {
    organizationOperationCountries(organizationId: $organizationId) {
      id
      country
      country_code
      iso_code
      currency
      currency_code
      currency_symbol
      phone_code
    }
  }
`;

export const GET_ORGANIZATION_BY_SLUG_MIN = gql`
  query OrganizationBySlugMinimal($slug: String!) {
    organizationBySlugMinimal(slug: $slug) {
      country
      custom_domain
      industry
      id
      logo
      name
      metadata
      plan
      primary_color
      slug
      size
      tenant_type
      website
      timezone
    }
  }
`;

/**
 * TypeScript Types
 */

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  tenant_type: string;
  industry?: string;
  logo?: string;
  primary_color?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  memberRole?: string;
}

export interface Member {
  id: string;
  userId: string;
  organizationId: string;
  role: string;
  email: string;
  invitedAt: string;
  joinedAt?: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface Permission {
  permission: string;
  resource: string;
  action: string;
}

export interface UpdateOrganizationInput {
  name?: string;
  slug?: string;
  industry?: string;
  tenant_type?: string;
  logo?: string;
  size?: string;
  website?: string;
  primary_color?: string;
  custom_domain?: string;
  currency?: string;
  timezone?: string;
  country?: string;
  operation_countries?: string[];
  metadata?: Record<string, any>;
}
